import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { FavoriteHeartButton } from '@/shared/components/FavoriteHeartButton'
import { AvailabilityBadge, ConfiguredLimitBadge, PriceOrDiscountBlock, ServiceStatusBadge } from '@/shared/components/cse'
import { useLongStayHintLabel } from '@/shared/components/cse/useLongStayHintLabel'
import { useSubsidyMentionVisible, useUnsubsidizedPriceVisible } from '@/shared/api/brandQueries'
import { useReservationSubsidyEnabled } from '@/shared/hooks/useReservationSubsidyEnabled'
import { formatMoneyCents } from '@/shared/lib/formatMoney'
import { formatIsoDateDisplay } from '@/shared/lib/datetime'
import { resolveAssetUrl } from '@/shared/lib/resolveAssetUrl'
import { PictureImg } from '@/shared/components/ui/picture-img'
import { propertyCardNightlyLabel } from '@/shared/lib/propertyPriceLabels'
import { propertyCardImage } from '@/features/reservation/utils/propertyCardImage'
import { formatDisplayPriceHint } from '@/features/reservation/utils/formatDisplayPriceHint'
import type { PropertyDetail } from '@/features/reservation/types/property'
import { CatalogCardBody, CatalogCardFooter } from '@/shared/components/CatalogCardLayout'
import { createLazyThemedIcon } from '@/shared/theme/resolveThemedIcon'

const MapPinIcon = createLazyThemedIcon('map-pin')
const HeartIcon = createLazyThemedIcon('heart')

export function HorizonOfferCard({
  property,
  nights,
  actionSearch,
  priority = false,
  favorited = false,
}: {
  property: PropertyDetail
  nights?: number
  actionSearch?: Record<string, unknown>
  priority?: boolean
  favorited?: boolean
}) {
  const { t } = useTranslation()
  const subsidyEnabled = useReservationSubsidyEnabled()
  const subsidyMentionVisible = useSubsidyMentionVisible()
  const unsubsidizedPriceVisible = useUnsubsidizedPriceVisible()
  const showSubsidyMention = subsidyEnabled && subsidyMentionVisible
  const cover = propertyCardImage(property)
  const currency = property.pricing.currency ?? 'EUR'
  const hasVariablePricing = property.pricing.has_variable_pricing === true
  const referenceCents = property.pricing.nightly_after_subsidy_cents
  const displayCents = hasVariablePricing
    ? (property.pricing.nightly_min_after_subsidy_cents ?? referenceCents)
    : referenceCents
  const money = (cents: number) => formatMoneyCents(cents, currency)
  const priceValue = propertyCardNightlyLabel(t, money(displayCents), {
    showSubsidyMention,
    hasVariablePricing,
    priceGranularity: property.price_granularity,
  })
  const compareAt =
    subsidyEnabled &&
    unsubsidizedPriceVisible &&
    property.base_price_cents != null &&
    property.base_price_cents !== displayCents
      ? money(property.base_price_cents)
      : undefined
  const pricedAsStay = property.price_granularity === 'per_stay'
  const longStayHelper = useLongStayHintLabel(property.long_stay_discount_hint, currency)
  const helper = [
    nights && nights > 0
      ? t('reservation.properties.card.total', {
          total: money(pricedAsStay ? displayCents : displayCents * nights),
          nights,
        })
      : undefined,
    longStayHelper,
    property.next_available_date
      ? t('reservation.properties.card.next_available', {
          date: formatIsoDateDisplay(property.next_available_date),
        })
      : undefined,
    formatDisplayPriceHint(t, property.pricing.display),
  ]
    .filter(Boolean)
    .join(' · ')
  const detailTo = `/reservation/properties/${encodeURIComponent(property.id)}`
  const amenityLabels = property.amenities
    .slice(0, 2)
    .map((amenity) => t(`reservation.properties.amenities.${amenity}`, { defaultValue: amenity }))
  const imageSrc = cover.jpeg || cover.webp || ''

  return (
    <article className="group relative h-full rounded-[var(--brand-radius-card)] bg-[var(--brand-background)]">
      <Link
        to={detailTo}
        search={actionSearch}
        className="flex h-full flex-col text-inherit no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-ring)]"
        aria-label={t('reservation.properties.card.view_aria', { name: property.name })}
      >
        <div className="relative aspect-square overflow-hidden rounded-[var(--brand-radius-card)] bg-[var(--brand-surface-strong)] sm:aspect-[4/3]">
          {imageSrc ? (
            <PictureImg
              webpSrc={cover.webp ? resolveAssetUrl(cover.webp) : undefined}
              src={resolveAssetUrl(imageSrc)}
              alt=""
              aria-hidden="true"
              className="size-full object-cover transition duration-200 group-hover:scale-[1.02]"
              loading={priority ? 'eager' : 'lazy'}
              decoding="async"
              sizes="(min-width: 1280px) 280px, (min-width: 640px) 50vw, 100vw"
              width={640}
              height={480}
            />
          ) : (
            <img
              src="/images/property-placeholder.svg"
              alt=""
              aria-hidden="true"
              loading="lazy"
              decoding="async"
              className="size-full object-contain"
              width={640}
              height={480}
            />
          )}
          <div className="absolute left-3 top-3 flex flex-wrap gap-2">
            {property.is_featured ? (
              <span className="inline-flex min-h-8 items-center gap-1.5 rounded-[var(--brand-radius-pill)] bg-[var(--brand-background)]/95 px-3 text-xs font-semibold text-[var(--brand-text-primary)]">
                <HeartIcon className="size-3.5 text-[var(--brand-primary-strong)]" aria-hidden />
                {t('reservation.properties.card.featured')}
              </span>
            ) : null}
            <ServiceStatusBadge
              label={
                property.is_active
                  ? t('reservation.properties.card.status_open')
                  : t('reservation.properties.card.status_closed')
              }
              tone={property.is_active ? 'open' : 'closed'}
            />
          </div>
        </div>

        <CatalogCardBody>
          <h3 className="m-0 truncate text-base font-semibold leading-snug text-[var(--brand-text-primary)]">
            {property.name}
          </h3>
          <p className="flex items-center gap-1.5 text-sm text-[var(--brand-text-secondary)]">
            <MapPinIcon className="size-4 shrink-0" aria-hidden />
            <span className="truncate">
              {t('reservation.properties.card.city_country', {
                city: property.city ?? t('reservation.properties.card.unknown_city'),
                country: property.country ?? t('reservation.properties.card.unknown_country'),
              })}
            </span>
          </p>
          <div className="flex flex-wrap gap-2">
            <ConfiguredLimitBadge
              label={t('reservation.properties.card.capacity', {
                min: property.capacity_min,
                max: property.capacity_max ?? property.capacity_min,
              })}
            />
            <AvailabilityBadge label={t('reservation.properties.card.availability_configured')} available={property.is_active} />
          </div>
          {amenityLabels.length > 0 ? (
            <p className="truncate text-sm text-[var(--brand-text-secondary)]">{amenityLabels.join(' · ')}</p>
          ) : null}
          <CatalogCardFooter className="border-[var(--brand-border-soft)] sm:items-end">
            <PriceOrDiscountBlock bare value={priceValue} compareAt={compareAt} helper={helper || undefined} />
          </CatalogCardFooter>
        </CatalogCardBody>
      </Link>
      <FavoriteHeartButton propertyId={property.id} variant="overlay" isFavorite={favorited || undefined} />
    </article>
  )
}
